import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Sparkles,
  Sunrise,
  Utensils,
  Moon,
  Cookie,
  Plus,
} from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/Skeleton'
import { useMealEntries } from '../../hooks/useMealEntries'
import { useProfile } from '../../hooks/useProfile'
import { MealForm } from './components/MealForm'
import { MealBuilder } from './components/MealBuilder'
import { MealCard } from './components/MealCard'
import { MacroRings } from './components/MacroRings'
import MealCoach from './components/MealCoach'
import FridgeRecipes from './components/FridgeRecipes'
import { SUGGESTED_TARGETS } from '../../lib/myFoods'

const MEAL_SLOTS = [
  { type: 'breakfast', icon: Sunrise },
  { type: 'lunch', icon: Utensils },
  { type: 'dinner', icon: Moon },
  { type: 'snack', icon: Cookie },
]

function todayLocal() {
  return new Date().toLocaleDateString('en-CA')
}

function shiftDate(iso, days) {
  const d = new Date(`${iso}T00:00:00`)
  d.setDate(d.getDate() + days)
  return d.toLocaleDateString('en-CA')
}

function formatLongDate(iso, lang) {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(lang || 'en', {
      weekday: 'short', month: 'short', day: 'numeric',
    })
  } catch {
    return iso
  }
}

export function MealsTab() {
  const { t, i18n } = useTranslation()
  const { entries, loading, addMeal, updateMeal, deleteMeal } = useMealEntries()
  const { profile, upsertMacroTargets } = useProfile()
  const [selectedDate, setSelectedDate] = useState(todayLocal())
  // createMode: null (not creating) | { mode: 'builder' | 'manual', mealType: string|null }
  // Builder is the default; "Add custom food" inside the builder swaps to manual.
  const [createMode, setCreateMode] = useState(null)
  const [editing, setEditing] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const startCreate = (mealType = null) => setCreateMode({ mode: 'builder', mealType })
  const switchToManual = () =>
    setCreateMode((prev) => (prev ? { ...prev, mode: 'manual' } : { mode: 'manual', mealType: null }))
  const cancelCreate = () => setCreateMode(null)

  const mealsForDay = useMemo(
    () => entries.filter((m) => m.date === selectedDate),
    [entries, selectedDate]
  )

  const grouped = useMemo(() => {
    const map = { breakfast: [], lunch: [], dinner: [], snack: [] }
    for (const m of mealsForDay) {
      if (map[m.meal_type]) map[m.meal_type].push(m)
    }
    return map
  }, [mealsForDay])

  // Top 5 unique-by-name most-recent meals across the user's history.
  // entries is already sorted newest-first by date+created_at, so the
  // first occurrence of each name is the latest.
  const recentFoods = useMemo(() => {
    const seen = new Map()
    for (const meal of entries) {
      if (!seen.has(meal.name)) {
        seen.set(meal.name, {
          name: meal.name,
          kcal: meal.kcal,
          protein: meal.protein_g,
          carbs: meal.carbs_g,
          fat: meal.fat_g,
        })
      }
      if (seen.size >= 5) break
    }
    return Array.from(seen.values())
  }, [entries])

  if (loading && entries.length === 0) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  }

  const handleCreate = async (values) => {
    setSubmitting(true)
    const ok = await addMeal(values)
    setSubmitting(false)
    if (ok) setCreateMode(null)
  }

  const handleUpdate = async (values) => {
    if (!editing) return
    setSubmitting(true)
    const ok = await updateMeal(editing.id, values)
    setSubmitting(false)
    if (ok) setEditing(null)
  }

  const isToday = selectedDate === todayLocal()

  // Banner: when targets aren't set yet AND the user has suggested targets
  // baked into their myFoods.json, offer one-tap apply. Hides as soon as
  // any target is saved.
  const hasAnyTarget =
    profile?.kcal_target != null ||
    profile?.protein_target_g != null ||
    profile?.carbs_target_g != null ||
    profile?.fat_target_g != null
  const showSuggestedBanner = !hasAnyTarget && SUGGESTED_TARGETS

  const applySuggestedTargets = () => {
    if (!SUGGESTED_TARGETS) return
    upsertMacroTargets({
      kcal: SUGGESTED_TARGETS.kcal,
      protein: SUGGESTED_TARGETS.protein_g,
      carbs: SUGGESTED_TARGETS.carbs_g,
      fat: SUGGESTED_TARGETS.fat_g,
    })
  }

  return (
    <div className="space-y-4">
      {/* Date navigator */}
      <Card>
        <CardContent className="p-2 flex items-center gap-1">
          <button
            type="button"
            onClick={() => setSelectedDate(shiftDate(selectedDate, -1))}
            className="p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
            aria-label="Previous day"
          >
            <ChevronLeft className="w-4 h-4 rtl:-scale-x-100" />
          </button>
          <div className="flex-1 flex items-center justify-center gap-2 text-sm text-foreground font-medium">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            {isToday ? t('phoenixTracker.meals.today') : formatLongDate(selectedDate, i18n.language)}
          </div>
          <button
            type="button"
            onClick={() => setSelectedDate(shiftDate(selectedDate, 1))}
            disabled={isToday}
            className="p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Next day"
          >
            <ChevronRight className="w-4 h-4 rtl:-scale-x-100" />
          </button>
        </CardContent>
      </Card>

      {/* One-tap "Apply suggested targets" banner. Only shows when targets
          are unset and the JSON ships a dailyTargets block (it does). */}
      {showSuggestedBanner && (
        <Card className="border-app-primary-500/40 bg-app-primary-500/5">
          <CardContent className="p-3 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-app-primary-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-foreground" dir="auto">
                {t('phoenixTracker.meals.targets.suggestedTitle')}
              </p>
              <p className="text-[11px] text-muted-foreground font-mono" dir="auto">
                <bdi>{SUGGESTED_TARGETS.kcal} {t('phoenixTracker.meals.kcalUnit')}</bdi>
                <span className="mx-1.5">·</span>
                <bdi>
                  {t('phoenixTracker.meals.macrosShort', {
                    protein: SUGGESTED_TARGETS.protein_g,
                    carbs: SUGGESTED_TARGETS.carbs_g,
                    fat: SUGGESTED_TARGETS.fat_g,
                  })}
                </bdi>
              </p>
            </div>
            <Button
              size="sm"
              onClick={applySuggestedTargets}
              className="bg-app-primary-500 hover:bg-app-primary-600 text-white flex-shrink-0"
            >
              {t('phoenixTracker.meals.targets.suggestedApply')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Daily totals + progress rings (when targets are set) */}
      <MacroRings
        meals={mealsForDay}
        profile={profile}
        onSaveTargets={(v) => upsertMacroTargets(v)}
        onClearTargets={() => upsertMacroTargets({ kcal: null, protein: null, carbs: null, fat: null })}
      />

      <MealCoach />

      <FridgeRecipes />

      {/* Create flow — builder by default, manual when switched from inside. */}
      {createMode && createMode.mode === 'builder' && (
        <Card>
          <CardContent className="p-4">
            <MealBuilder
              defaultDate={selectedDate}
              defaultType={createMode.mealType || 'breakfast'}
              onSubmit={handleCreate}
              onSwitchToManual={switchToManual}
              onCancel={cancelCreate}
              submitting={submitting}
            />
          </CardContent>
        </Card>
      )}

      {createMode && createMode.mode === 'manual' && (
        <Card>
          <CardContent className="p-4">
            <MealForm
              defaultDate={selectedDate}
              defaultType={createMode.mealType || 'breakfast'}
              recentFoods={recentFoods}
              onSubmit={handleCreate}
              onCancel={cancelCreate}
              submitting={submitting}
            />
          </CardContent>
        </Card>
      )}

      {/* Edit form */}
      {editing && (
        <Card>
          <CardContent className="p-4">
            <MealForm
              initialValues={editing}
              onSubmit={handleUpdate}
              onCancel={() => setEditing(null)}
              submitting={submitting}
            />
          </CardContent>
        </Card>
      )}

      {/* Per-slot cards — always render all 4 so the day has a stable
          shape. Filled slots show entries + an "Add another" link; empty
          slots are a single dashed-border tap target. */}
      {!createMode && !editing && MEAL_SLOTS.map(({ type, icon: SlotIcon }) => {
        const list = grouped[type] || []
        const slotName = t(`phoenixTracker.meals.types.${type}`)
        const slotKcal = list.reduce((sum, m) => sum + (Number(m.kcal) || 0), 0)
        const isEmpty = list.length === 0

        if (isEmpty) {
          return (
            <button
              key={type}
              type="button"
              onClick={() => startCreate(type)}
              className="w-full block rounded-lg border-2 border-dashed border-input bg-muted/20 hover:bg-muted/40 hover:border-app-primary-500/50 transition-colors p-5 text-center"
            >
              <SlotIcon className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm font-medium text-foreground" dir="auto">
                {slotName}
              </p>
              <p className="text-xs text-muted-foreground mt-1" dir="auto">
                {t('phoenixTracker.meals.slot.tapToLog', { slot: slotName })}
              </p>
            </button>
          )
        }

        return (
          <Card key={type}>
            <CardContent className="p-3 sm:p-4 space-y-3">
              {/* Slot header — icon + name on the start side, kcal subtotal
                  on the end side. dir="auto" lets the paragraph pick its
                  base direction from the localized slot name. */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <SlotIcon className="w-4 h-4 text-app-primary-500 flex-shrink-0" />
                  <p className="text-sm font-semibold text-foreground truncate" dir="auto">
                    {slotName}
                  </p>
                </div>
                <p className="text-xs font-mono text-app-primary-500 font-semibold flex-shrink-0" dir="auto">
                  <bdi>{Math.round(slotKcal)} {t('phoenixTracker.meals.kcalUnit')}</bdi>
                </p>
              </div>

              {/* Entry list */}
              <div className="space-y-2">
                {list.map((meal) => (
                  <MealCard
                    key={meal.id}
                    meal={meal}
                    onEdit={(m) => setEditing(m)}
                    onDelete={deleteMeal}
                  />
                ))}
              </div>

              {/* Add another */}
              <button
                type="button"
                onClick={() => startCreate(type)}
                className="w-full flex items-center justify-center gap-1 text-xs text-app-primary-500 hover:text-app-primary-600 font-medium py-2 rounded-md hover:bg-app-primary-500/10 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                {t('phoenixTracker.meals.slot.addAnother')}
              </button>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
